import {
  Component,
  forwardRef,
  Input,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  CdkDragDrop,
  moveItemInArray,
} from '@angular/cdk/drag-drop';

export interface ImageDragAndDropItem {
  location: string;
  data?: string;
  position: number;

  previewUrl?: string;
  filename?: string;
  size?: number;
  encodedSize?: number;
  isNew?: boolean;
}

@Component({
  selector: 'app-image-drag-and-drop',
  templateUrl: './image-drag-and-drop.component.html',
  styleUrls: ['./image-drag-and-drop.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageDragAndDropComponent),
      multi: true,
    },
  ],
})
export class ImageDragAndDropComponent implements ControlValueAccessor {
  @Input() baseUrl = '';
  @Input() multiple = true;

  // limit jednego pliku, np. 5 MB
  @Input() maxSize = 5 * 1024 * 1024;

  // limit wszystkich zdjęć razem: już zapisanych + nowych, np. 8 MB
  @Input() maxTotalSize = 8 * 1024 * 1024;

  @Input() acceptMime = 'image/jpeg,image/png,image/webp';

  images: ImageDragAndDropItem[] = [];
  disabled = false;
  errors: string[] = [];

  private onChange: (value: ImageDragAndDropItem[]) => void = () => {};
  private onTouched: () => void = () => {};

  get totalSize(): number {
    return this.images.reduce((sum, image) => sum + (image.size ?? 0), 0);
  }

  writeValue(value: ImageDragAndDropItem[] | null): void {
    this.images = (value ?? [])
      .slice()
      .sort((a, b) => (a.position ?? 999) - (b.position ?? 999))
      .map((image, index) => ({
        ...image,
        position: index + 1,
        previewUrl: image.previewUrl ?? this.getPreviewUrl(image),
        isNew: !!image.data,
        size: image.size ?? 0,
        encodedSize: image.encodedSize ?? this.getDataUrlByteSize(image.data),
      }));
  }

  registerOnChange(fn: (value: ImageDragAndDropItem[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onImagesSelect(fileList: FileList | null): void {
    if (!fileList || this.disabled) {
      return;
    }

    this.errors = [];

    const files = Array.from(fileList);
    let plannedTotalSize = this.totalSize;

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        this.errors.push(`Plik "${file.name}" nie jest obrazkiem.`);
        continue;
      }

      if (!this.isAcceptedMime(file)) {
        this.errors.push(`Plik "${file.name}" ma niedozwolony format.`);
        continue;
      }

      if (file.size > this.maxSize) {
        this.errors.push(
          `Plik "${file.name}" jest za duży. Maksymalnie ${this.formatBytes(this.maxSize)}.`
        );
        continue;
      }

      const nextTotalSize = plannedTotalSize + file.size;

      if (nextTotalSize > this.maxTotalSize) {
        this.errors.push(
          `Nie można dodać "${file.name}". Łączny rozmiar zdjęć przekroczy ${this.formatBytes(this.maxTotalSize)}.`
        );
        continue;
      }

      plannedTotalSize = nextTotalSize;

      const reader = new FileReader();

      reader.onload = () => {
        const dataUrl = reader.result?.toString() ?? '';
        const encodedSize = this.getDataUrlByteSize(dataUrl);
        const nextTotalAfterRead = this.totalSize + file.size;

        if (nextTotalSize > this.maxTotalSize) {
          this.errors.push(
            `Nie można dodać "${file.name}". Łączny rozmiar zdjęć przekroczy ${this.formatBytes(this.maxTotalSize)}.`
          );
          return;
        }

        this.images.push({
          location: '',
          data: dataUrl,
          previewUrl: dataUrl,
          filename: file.name,
          size: file.size,
          encodedSize,
          isNew: true,
          position: this.images.length + 1,
        });

        this.emitValue();
      };

      reader.readAsDataURL(file);

      if (!this.multiple) {
        break;
      }
    }

    this.onTouched();
  }

  dropImage(event: CdkDragDrop<ImageDragAndDropItem[]>): void {
    if (this.disabled) {
      return;
    }

    moveItemInArray(this.images, event.previousIndex, event.currentIndex);
    this.emitValue();
    this.onTouched();
  }

  removeImage(index: number): void {
    if (this.disabled) {
      return;
    }

    this.images.splice(index, 1);
    this.clearResolvedTotalSizeErrors();
    this.emitValue();
    this.onTouched();
  }

  formatBytes(bytes: number): string {
    if (!bytes) {
      return '0 MB';
    }

    const mb = bytes / 1024 / 1024;

    return `${mb.toFixed(2)} MB`;
  }

  private emitValue(): void {
    this.images = this.images.map((image, index) => ({
      ...image,
      position: index + 1,
    }));

    this.onChange(
      this.images.map((image, index) => ({
        location: image.location ?? '',
        data: image.data,
        position: index + 1,
        size: image.size,
        encodedSize: image.encodedSize,
      }))
    );
  }

  private getPreviewUrl(image: ImageDragAndDropItem): string {
    if (image.data) {
      return image.data;
    }

    if (!image.location) {
      return '';
    }

    if (
      image.location.startsWith('http://') ||
      image.location.startsWith('https://') ||
      image.location.startsWith('data:image')
    ) {
      return image.location;
    }

    return `${this.baseUrl.replace(/\/$/, '')}/${image.location.replace(/^\//, '')}`;
  }

  private isAcceptedMime(file: File): boolean {
    if (!this.acceptMime) {
      return true;
    }

    return this.acceptMime
      .split(',')
      .map((type) => type.trim())
      .includes(file.type);
  }


  private getDataUrlByteSize(dataUrl?: string): number {
    if (!dataUrl) {
      return 0;
    }

    return new Blob([dataUrl]).size;
  }

  private clearResolvedTotalSizeErrors(): void {
    if (this.totalSize > this.maxTotalSize) {
      return;
    }

    this.errors = this.errors.filter(
      (error) => !error.includes('Łączny rozmiar zdjęć przekroczy')
    );
  }
}
